import type { PaymentCardBrand } from '@frontend/api-client';
import { buildCancellationURLParams, getMembershipPlanType } from '@frontend/api-client';
import { appStoreLinks } from '@frontend/constants';
import { buildDisplayableMembership } from '@frontend/react-domain';
import { useVisibilityController } from '@frontend/react/hooks';
import {
  ConfirmationModal,
  Header,
  Icon,
  LinkButton,
  PageContainer,
  XStack,
  YStack,
  isAndroid,
  isIOS,
  isMobile,
} from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import { CreditCard, CreditCardModal, InviteHouseholdMemberModal } from '@frontend/web/components';
import { MembershipPlanCard } from '@frontend/web/components/DomainSpecific/Onboarding/MembershipPlanCard';
import { DeleteAccountModal } from '@frontend/web/components/DomainSpecific/Settings/DeleteAccountModal';
import {
  useAccessControl,
  useAnalytics,
  useAuthentication,
  useDeleteCardMutation,
  useListCardsQuery,
  useRetrieveClientQuery,
  useRetrieveHouseholdQuery,
  useUpdateDefaultCardMutation,
} from '@frontend/web/hooks';
import { Env, handleMutation } from '@frontend/web/utils';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

const Payment = () => {
  const { data: client } = useRetrieveClientQuery();
  const { isClientPayingSubscriber } = useAccessControl();
  const { data: cards, isLoading: isCardsLoading } = useListCardsQuery();
  const [updateDefaultCard, { isLoading: isUpdateDefaultCardLoading }] = useUpdateDefaultCardMutation();
  const [deleteCard, { isLoading: isDeleteCardLoading }] = useDeleteCardMutation();
  const { data: household, isLoading: isHouseholdLoading } = useRetrieveHouseholdQuery(client?.household as string);
  const { track } = useAnalytics();
  const { logout } = useAuthentication();
  const [focusedCardId, setFocusedCardId] = useState<string>();

  const planType = useMemo(() => (client ? getMembershipPlanType(client) : undefined), [client?.priceId]);
  const currentPlanDetails = useMemo(() => buildDisplayableMembership(client), [client]);
  const isPendingCancellation =
    currentPlanDetails?.pendingChange && currentPlanDetails?.nextMembershipPlan?.isCancelled;
  const isInviteVisible = !!(
    client?.isHouseholdOwner &&
    household?.currentUsers &&
    household?.maxUsers &&
    household.currentUsers < household.maxUsers
  );

  const [isCreateInviteModalOpen, { close: closeCreateInviteModal, open: openCreateInviteModal }] =
    useVisibilityController();
  const [isCreditCardFormModalOpen, { close: closeCreditCardFormModal, open: openCreditCardFormModal }] =
    useVisibilityController();

  const [isDeleteCreditCardModalOpen, { close: closeDeleteCreditCardModal, open: openDeleteCreditCardModal }] =
    useVisibilityController();
  const [isDeleteAccountModalOpen, { close: closeDeleteAccountModal, open: openDeleteAccountModal }] =
    useVisibilityController();

  const declinedCard = useMemo(() => cards?.find((card) => card.dateDeclined), [cards]);

  useEffect(() => {
    if (declinedCard) {
      setFocusedCardId(declinedCard.id);
      openCreditCardFormModal();
    }
  }, [declinedCard]);

  const handleUpdateDefaultCard = (id: string) => {
    setFocusedCardId(id);
    handleMutation({
      mutation: () => updateDefaultCard(id).unwrap(),
      onSuccess: () => setFocusedCardId(undefined),
      successMessage: 'Default card updated.',
    });
  };

  const handleDeleteCard = (id: string) => {
    setFocusedCardId(id);
    handleMutation({
      mutation: () => deleteCard(id).unwrap(),
      onSuccess: () => setFocusedCardId(undefined),
      successMessage: 'Card deleted.',
    });
  };

  const handleEditCard = (id: string) => {
    setFocusedCardId(id);
    openCreditCardFormModal();
  };

  const handleCancelSubscription = () => {
    if (!client) return;
    const cancellationFormId = Env.VITE_CANCELLATION_FORM_ID.trim();
    if (!cancellationFormId) return;
    const url = `https://form.typeform.com/to/${cancellationFormId}#` + buildCancellationURLParams(client);
    window.open(url, '_blank');
  };

  const handleCloseCreditCardFormModal = () => {
    if (!declinedCard) {
      closeCreditCardFormModal();
      setFocusedCardId(undefined);
    }
  };

  const handleLogout = () => {
    track({ event: 'logout', topic: 'auth' });
    logout();
  };

  const handleOpenApp = () => {
    if (isIOS) {
      window.open(appStoreLinks.ios);
    } else if (isAndroid) {
      window.open(appStoreLinks.android);
    }
  };

  return (
    <PageContainer
      HeaderComponent={
        <XStack alignItems="center" justifyContent="between">
          <Header title="Payment" titleSize="h4" />
          {isMobile ? (
            <LinkButton
              className="mr-lg"
              color="danger"
              label="Logout"
              leftIconName="IconLogout"
              onClick={handleLogout}
              type="h5"
            />
          ) : null}
        </XStack>
      }
      isLoading={isCardsLoading || isHouseholdLoading}
      isScrollable={false}
    >
      <YStack gapY="lg">
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <YStack className="xl:max-w-[1000px]" gapY="lg">
          <XStack className="w-full items-center" gapX="md">
            {planType ? (
              <MembershipPlanCard
                className="flex-1"
                currentClientId={client?.id}
                hideBenefitDetails
                hidePrice
                household={household}
                isSelected={false}
                plan={planType}
                promoDetails={undefined}
                showCurrentUsers
                showOwner={!!household}
              />
            ) : null}
            {isInviteVisible ? (
              <Icon accessibilityLabel="Invite household member" name="IconUserPlus" onClick={openCreateInviteModal} />
            ) : null}
          </XStack>
          <XStack justifyContent="end">
            <LinkButton
              label="Add new card"
              leftIconName="IconPlus"
              leftIconProps={{ size: 'md' }}
              onClick={openCreditCardFormModal}
              type="h6"
            />
          </XStack>
          <YStack className="gap-md">
            {cards?.map((card) => (
              <CreditCard
                brand={card.brand as PaymentCardBrand}
                expiryDate={`${card.month}/${card.year}`}
                isDefault={card.isDefault}
                isDeleteCardLoading={!!(isDeleteCardLoading && focusedCardId === card.id)}
                isMakeDefaultLoading={!!(isUpdateDefaultCardLoading && focusedCardId === card.id)}
                key={card.id}
                lastFourDigits={card.lastFour}
                onDelete={() => {
                  setFocusedCardId(card.id);
                  openDeleteCreditCardModal();
                }}
                onEditCard={() => handleEditCard(card.id)}
                onMakeDefault={() => handleUpdateDefaultCard(card.id)}
              />
            ))}
          </YStack>
        </YStack>
        {isClientPayingSubscriber && !isPendingCancellation ? (
          <YStack className="items-start">
            <LinkButton color="tertiary" label="Cancel subscription" onClick={handleCancelSubscription} type="p-sm" />
          </YStack>
        ) : null}
        <YStack alignItems="start">
          <LinkButton
            color="danger"
            label="Delete account"
            onClick={openDeleteAccountModal}
            textClassName={safeCss('font-normal text-p-sm text-textError')}
          />
        </YStack>
      </YStack>
      {isMobile && (isIOS || isAndroid) ? (
        <YStack className="pb-md w-full items-center">
          <YStack
            accessibilityLabel="Open mobile app"
            className="py-sm px-md bg-surfaceSecondary shadow-three text-h6 text-textPrimary rounded-md"
            onClick={handleOpenApp}
          >
            Open app
          </YStack>
        </YStack>
      ) : null}
      {household?.id ? (
        <InviteHouseholdMemberModal
          householdId={household.id}
          isOpen={isCreateInviteModalOpen}
          onClose={closeCreateInviteModal}
        />
      ) : null}
      <CreditCardModal
        declinedCard={!!declinedCard && focusedCardId === declinedCard.id}
        focusedCardId={focusedCardId}
        isOpen={isCreditCardFormModalOpen}
        onClose={handleCloseCreditCardFormModal}
      />
      <DeleteAccountModal isOpen={isDeleteAccountModalOpen} onClose={closeDeleteAccountModal} />
      <ConfirmationModal
        cancelLabel="Cancel"
        isOpen={isDeleteCreditCardModalOpen}
        onClickCancel={closeDeleteCreditCardModal}
        onClickSubmit={() => {
          handleDeleteCard(focusedCardId!);
        }}
        onClose={closeDeleteCreditCardModal}
        submitLabel="Delete"
        subtitle="Are you sure you want to delete this payment method?"
        title="Delete payment method"
        variant="destructive"
      />
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/settings/payments')({
  component: Payment,
});
